/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useEffect } from "react";

import * as React from "react";
import styled from "styled-components";
import thankYouGradientWebm from "components/ThankYouGradient.webm";
import thankYouGradientMp4 from "components/ThankYouGradient.mp4";
import thankYouChimeMp3 from "components/ThankYouChime.mp3";
import thankYouGradientPoster from "components/ThankYouGradient.jpg";
import { devices } from "components/shared/Styles";
import { useCurrentUser } from "actions/CurrentUserActions";
import { useHistory } from "react-router-dom";
import { AppPath } from "components/AppPath";

const Popup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  position: relative;
  height: min(600px, calc(100% - 80px));
  max-height: fit-content;
  margin: 50px;

  ${devices.small} {
    padding: 10px;
    width: min(1000px, calc(100% - 65px));
    height: 250px;
    margin: 0;
  }
`;

const VideoContainer = styled.div`
  border-radius: 24px;
  overflow: hidden;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  box-shadow: var(--phonos-box-shadow);
`;

const Video = styled.video.attrs({
  autoPlay: true,
  loop: true,
  muted: true,
  controls: false,
  disablePictureInPicture: true,
  playsInline: true,
  controlsList: "nofullscreen",
  poster: thankYouGradientPoster,
})`
  object-fit: cover;
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  // Fix for SWC bug: https://adobeprovideo.slack.com/archives/CESK60MQD/p1689197352956859
  font-family:
    adobe-clean,
    "Source Sans Pro",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Ubuntu,
    "Trebuchet MS",
    "Lucida Grande",
    sans-serif !important;
  -webkit-font-smoothing: antialiased;

  color: var(--spectrum-gray-800);
  background-color: var(--spectrum-global-color-static-white);
  border-radius: 42px;
  padding: 60px;
  position: relative;
  font-size: 18px;
  line-height: 30px;
  max-height: fit-content;
  margin: 70px;

  ${devices.small} {
    font-size: 16px;
    line-height: 22px;
    padding: 32px;
    border-radius: 16px;
  }
`;

const Title = styled.h2`
  font-size: 156px;
  font-weight: 900;
  line-height: 120px;
  padding-bottom: 80px;
  letter-spacing: -5px;

  color: transparent;
  background: linear-gradient(45deg, #ecda51 0%, #e55fcb 47%, #5158ec 100%);
  -webkit-background-clip: text;
  background-clip: text;
  background-size: 200% auto;
  animation: text-clip-animation 5s ease-in-out infinite alternate;

  @keyframes text-clip-animation {
    0% {
      background-position: 0 50%;
    }
    100% {
      background-position: 100% 50%;
    }
  }

  ${devices.small} {
    font-size: 40px;
    line-height: 60px;
    padding-bottom: 40px;
    margin: 8px;
    width: 100%;
  }
`;

const CloseButton = styled.button.attrs({
  "aria-label": I18n.t("checkout.thank_you.close"),
})`
  position: absolute;
  width: 40px;
  height: 40px;
  top: -20px;
  right: -20px;
  border-radius: 50%;
  border: none;
  background-color: var(--spectrum-global-color-static-white);
  box-shadow: 0 8px 16px #00000029;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ThankYou: React.FC<{
  closeCheckout: () => void;
}> = ({ closeCheckout }) => {
  const history = useHistory();
  const { refreshCurrentUser } = useCurrentUser();
  const componentRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sound: HTMLAudioElement = new Audio(thankYouChimeMp3);
    sound
      .play()
      .then(() => {
        if (componentRef.current) {
          componentRef.current.setAttribute("data-audio-played", "true");
        }
      })
      .catch((error) => {
        console.warn("Error playing thankYouChime:", error);
      });
  }, []);

  const onClick = async (): Promise<void> => {
    closeCheckout();
    await refreshCurrentUser();
  };
  return (
    <Popup ref={componentRef}>
      <VideoContainer>
        <Video>
          <source src={thankYouGradientMp4} />
          <source src={thankYouGradientWebm} />
        </Video>
      </VideoContainer>
      <Content>
        <Title>{I18n.t("checkout.thank_you.title")}</Title>
        {I18n.t("checkout.thank_you.enjoy")}
        <br />
        {I18n.t("checkout.thank_you.team")}
      </Content>
      <CloseButton onClick={onClick}>
        <sp-icon-close />
      </CloseButton>
    </Popup>
  );
};
